import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { titleCase } from '../../lib/format'
import type { EquipmentSummary } from '../../types'
import { Alert, Badge, Card, CardBody, CardHeader, EmptyState, Input, Select, Skeleton } from '../../components/ui'
import { WrenchIcon } from '../../components/icons'

/* Equipment maintenance reference - port of admin/equipment.html
 * (admin/routes.py equipment(), grouped by equipment_class). Reuses the
 * existing GET /api/v1/equipment endpoint (already built for the AI
 * troubleshooting sidebar, session-authenticated, not role-restricted -
 * only linked from the admin nav here). */

interface EquipmentListResponse { equipment: EquipmentSummary[] }

export default function AdminEquipmentGuide() {
  const [cls, setCls] = useState('')
  const [search, setSearch] = useState('')

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['equipment', 'list'],
    queryFn: () => api.get<EquipmentListResponse>('/equipment'),
  })

  const all = data?.equipment ?? []
  const classes = useMemo(
    () => Array.from(new Set(all.map((e) => e.equipment_class || 'General'))).sort(),
    [all],
  )
  const q = search.trim().toLowerCase()
  const items = all.filter((e) => {
    if (cls && (e.equipment_class || 'General') !== cls) return false
    if (!q) return true
    return (e.equipment_name || '').toLowerCase().includes(q)
      || (e.manufacturer || '').toLowerCase().includes(q)
      || (e.model || '').toLowerCase().includes(q)
  })

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Equipment guide</h1>
          <p className="page-head__sub">Maintenance reference — assemblies, known problems, repair procedures and PM schedules.</p>
        </div>
      </div>

      <div className="filter-scroll" style={{ marginBottom: 'var(--sp-5)' }}>
        <Input label="Search" value={search} onChange={(e) => setSearch(e.target.value)}
               placeholder="Name, manufacturer, model…" />
        <Select label="Class" value={cls} onChange={(e) => setCls(e.target.value)}>
          <option value="">All classes ({all.length})</option>
          {classes.map((c) => <option key={c} value={c}>{c} ({all.filter((e) => (e.equipment_class || 'General') === c).length})</option>)}
        </Select>
      </div>

      <Card>
        <CardHeader title="Equipment" sub={items.length ? `${items.length} entries` : undefined} />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(error as Error).message}</Alert></div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack"><Skeleton h={20} /><Skeleton h={20} /></div>
          ) : items.length === 0 ? (
            <EmptyState icon={<WrenchIcon size={22} />} title="No equipment found" body="The maintenance knowledge base will appear here once populated." />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Name</th><th>Manufacturer</th><th>Model</th><th>Class</th><th className="table__num">Components</th><th /></tr></thead>
                <tbody>
                  {items.map((e) => (
                    <tr key={e.equipment_id}>
                      <td>
                        <Link to={`/equipment-guide/${e.equipment_id}`} style={{ fontWeight: 600 }}>
                          {e.equipment_name || e.equipment_id}
                        </Link>
                      </td>
                      <td>{e.manufacturer || '—'}</td>
                      <td>{e.model || '—'}</td>
                      <td>{titleCase(e.equipment_class) || '—'}</td>
                      <td className="table__num">{e.component_count ?? 0}</td>
                      <td style={{ textAlign: 'right' }}>
                        {!!e.critical_problems && (
                          <Badge tone="danger">{e.critical_problems} critical</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}
